import * as React from "react";
import {
  buildAdminErrorDiagnostic,
  logAdminErrorDiagnostic,
  type AdminErrorDiagnostic,
} from "../../lib/errorDiagnostics";
import { isUnauthorizedError } from "../../lib/errors";
import { Trans } from "react-i18next";

type State = {
  hasError: boolean;
  error?: Error | null;
  info?: React.ErrorInfo | null;
  diagnostic?: AdminErrorDiagnostic | null;
};

function currentInternalPath() {
  if (typeof window === "undefined") return "/dashboard";
  return `${window.location.pathname || "/"}${window.location.search || ""}${window.location.hash || ""}`;
}

function isMissingTenantContextError(error: unknown) {
  const maybeError = error as any;
  const code =
    typeof maybeError?.code === "string"
      ? maybeError.code.trim().toUpperCase()
      : "";
  const message =
    typeof maybeError?.message === "string"
      ? maybeError.message.trim().toLowerCase()
      : "";

  return (
    code === "TENANT_MISSING" ||
    code === "TENANT_CONTEXT_MISSING" ||
    message.includes("tenant id not found") ||
    message.includes("tenant context") ||
    message.includes("store context")
  );
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { hasError: false, error: null, info: null, diagnostic: null };

  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error,
      diagnostic: buildAdminErrorDiagnostic(error),
    };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const diagnostic =
      this.state.diagnostic ?? buildAdminErrorDiagnostic(error);
    this.setState({ info, diagnostic });
    logAdminErrorDiagnostic(diagnostic, error, info);

    if (typeof window !== "undefined" && isUnauthorizedError(error)) {
      const returnTo = currentInternalPath();
      if (!returnTo.startsWith("/login")) {
        window.location.assign(
          `/login?reason=session-expired&returnTo=${encodeURIComponent(returnTo)}`,
        );
      }
      return;
    }

    if (typeof window !== "undefined" && isMissingTenantContextError(error)) {
      if (window.location.pathname !== "/dashboard") {
        window.location.assign("/dashboard");
      }
      return;
    }

    // Detect chunk load errors as a safety net if lazyWithRetry didn't catch it
    const message = error?.message || String(error);
    if (
      message.includes("Failed to fetch dynamically imported module") ||
      message.includes("error loading dynamically imported module") ||
      message.includes("Importing a module script failed")
    ) {
      const RELOAD_KEY = "storizone_last_chunk_reload";
      const now = Date.now();
      const lastReload = localStorage.getItem(RELOAD_KEY);

      if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
        localStorage.setItem(RELOAD_KEY, now.toString());
        window.location.reload();
      }
    }
  }

  copyDetails = async () => {
    const { diagnostic } = this.state;
    if (!diagnostic) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(diagnostic, null, 2));
    } catch {}
  };

  render() {
    if (!this.state.hasError) return this.props.children as React.ReactElement;
    const correlationId = this.state.diagnostic?.correlationId;
    return (
      <div
        role="alert"
        className="mx-auto my-10 max-w-xl rounded-md border p-5"
        style={{
          backgroundColor: "hsl(var(--color-destructive) / 0.08)",
          borderColor: "hsl(var(--color-destructive) / 0.25)",
        }}
      >
        <h2 className="text-lg font-semibold">
          <Trans i18nKey="ui:weCouldNotLoadThisPage">
            {"We could not load this page."}
          </Trans>
        </h2>
        <p
          className="mt-2 text-sm"
          style={{ color: "hsl(var(--color-destructive))" }}
        >
          <Trans i18nKey="ui:refreshOrReturnToDashboard">
            {"Refresh or return to dashboard."}
          </Trans>
        </p>
        {correlationId ? (
          <p className="mt-3 break-all rounded bg-background/70 px-2 py-1.5 font-mono text-xs text-muted-foreground">
            <Trans i18nKey="ui:referenceId">{"Reference ID:"}</Trans>{" "}
            {correlationId}
          </p>
        ) : null}
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            onClick={() => window.location.reload()}
            className="rounded-md border px-3 py-1.5 text-sm hover:bg-primary/10"
          >
            <Trans i18nKey="ui:refresh">{"Refresh"}</Trans>
          </button>
          <a
            href="/dashboard"
            className="rounded-md border px-3 py-1.5 text-sm hover:bg-primary/10"
          >
            <Trans i18nKey="ui:dashboard">{"Dashboard"}</Trans>
          </a>
          {correlationId ? (
            <button
              onClick={this.copyDetails}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-primary/10"
            >
              <Trans i18nKey="ui:copyReference">{"Copy reference"}</Trans>
            </button>
          ) : null}
        </div>
      </div>
    );
  }
}
